import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { homeFor, useAuth } from '../auth.jsx';
import AuthLayout from '../components/AuthLayout.jsx';
import Alert from '../components/ui/Alert.jsx';

const fmt = (secs) => `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
const secondsUntil = (iso) => (iso ? Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 1000)) : 0);

export default function VerifyEmail() {
  const auth = useAuth();
  const { user, verifyEmail, resendCode } = auth;
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || new URLSearchParams(location.search).get('email') || '';

  const [code, setCode] = useState('');
  const [expiresAt, setExpiresAt] = useState(location.state?.expires_at || null);
  const [resendAt, setResendAt] = useState(Date.now() + 1000 * (location.state?.resend_after ?? 60));
  const [now, setNow] = useState(Date.now());
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const submitted = useRef('');

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = async (value = code) => {
    if (!/^\d{6}$/.test(value) || busy) return;
    submitted.current = value;
    setBusy(true); setError(''); setNotice('');
    try {
      const me = await verifyEmail(email, value);
      navigate(homeFor(me), { replace: true });
    } catch (err) {
      setError(err.message);
      setCode('');
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const onChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    if (error) setError('');
    // Submit as soon as the sixth digit arrives (typed or pasted) — once per value
    if (digits.length === 6 && submitted.current !== digits) submit(digits);
  };

  const resend = async () => {
    setError(''); setNotice('');
    try {
      const res = await resendCode(email);
      setExpiresAt(res.expires_at);
      setResendAt(Date.now() + 1000 * (res.resend_after ?? 60));
      setCode(''); submitted.current = '';
      setNotice(`We’ve sent a new code to ${email}. Earlier codes no longer work.`);
      inputRef.current?.focus();
    } catch (err) {
      if (err.data?.resend_after) setResendAt(Date.now() + 1000 * err.data.resend_after);
      setError(err.message);
    }
  };

  if (user) return <Navigate to={homeFor(auth)} replace />;
  if (!email) return <Navigate to="/register" replace />;

  const left = secondsUntil(expiresAt);
  const resendIn = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const expired = expiresAt && left === 0;
  void now; // re-render each second for the countdowns

  return (
    <AuthLayout onSubmit={(e) => { e.preventDefault(); submit(); }}>
      <h1>Check your email</h1>
      <p className="muted">We&rsquo;ve sent a 6-digit code to <strong className="ink">{email}</strong>. Enter it below to finish creating your account.</p>

      {notice && <Alert tone="info">{notice}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}

      <div className="field">
        <label htmlFor="otp">Verification code</label>
        <input id="otp" ref={inputRef} className="otp-input" value={code} onChange={onChange}
          inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6}
          placeholder="••••••" aria-describedby="otp-hint" disabled={busy} />
        <p id="otp-hint" className="field-hint" aria-live="polite">
          {expired
            ? 'This code has expired. Send yourself a new one.'
            : expiresAt ? <>The code expires in <strong>{fmt(left)}</strong>.</> : 'The code expires 15 minutes after it was sent.'}
        </p>
      </div>

      <button type="submit" className="btn btn-primary btn-block" disabled={busy || code.length !== 6}>
        {busy ? 'Checking…' : 'Confirm email'}
      </button>

      <div className="otp-actions">
        <button type="button" className="link-button" onClick={resend} disabled={resendIn > 0}>
          {resendIn > 0 ? `Send a new code in ${resendIn}s` : 'Send a new code'}
        </button>
        <Link to="/register">Wrong email? Start again</Link>
      </div>
      <p className="muted small">Can&rsquo;t see it? Check your spam or junk folder. The email comes from SummitShield.</p>
    </AuthLayout>
  );
}
